import React, { useEffect, useState } from "react";
import { Header } from "../../components/Header/Header";
import { Navigator } from "../../components/Navigator/Navigator";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  adminGetAllUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminTransferDevelopments,
} from "../../context/Auth";
import Swal from "sweetalert2";
import { Plus, Pencil, Trash2, X, Building2, ChevronUp, ChevronDown, ChevronsUpDown, ArrowRightLeft, Eye, EyeOff } from "lucide-react";

const ROLES = ["user", "admin"];
const GIROS = ["Inmobiliaria", "Desarrolladora"];

function SortableTh({ label, sortKey, current, dir, onSort, className = "" }) {
  const active = current === sortKey;
  return (
    <th
      className={`pb-3 font-medium select-none cursor-pointer group ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 text-gray-500 group-hover:text-nemu-black transition-colors">
        {label}
        <span className={`transition-colors ${active ? "text-nemu-azul" : "text-gray-300 group-hover:text-gray-400"}`}>
          {active ? (
            dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
          ) : (
            <ChevronsUpDown size={13} />
          )}
        </span>
      </span>
    </th>
  );
}

// The enterprise profile form stores contact data in contact_email/contact_phone
// and location in city/state. The legacy email/phone/address columns are mostly
// empty, so read the live fields first and fall back to the legacy ones.
const entEmail = (ent) => ent?.contact_email || ent?.email || "";
const entPhone = (ent) => ent?.contact_phone || ent?.phone || "";
const entLocation = (ent) =>
  ent?.address || [ent?.city, ent?.state].filter(Boolean).join(", ") || "";

// Contraseña con leyenda "Copiar contraseña" (copia al portapapeles) y ojito
// para mostrarla en claro. Reutilizable en varias tablas.
function PasswordCopy({ password }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!password || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(password)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  if (!password) return <span className="text-[12px] text-gray-300">—</span>;

  return (
    <div className="flex items-center gap-1.5">
      {show ? (
        <span className="text-[12px] text-gray-700 font-mono truncate" title={password}>
          {password}
        </span>
      ) : (
        <button
          type="button"
          onClick={handleCopy}
          className={`text-[12px] ${copied ? "text-green-600" : "text-nemu-azul hover:underline"}`}
        >
          {copied ? "¡Copiado!" : "Copiar contraseña"}
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setShow((s) => !s); }}
        title={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        className="text-gray-400 hover:text-gray-700 shrink-0"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

// Celda "Accesos inventario": usuario de acceso (link_user) en negritas y,
// debajo, la contraseña (link_password) con copiar/mostrar.
function AccessCell({ linkUser, linkPassword }) {
  return (
    <td className="py-3 pr-4 max-w-[180px]">
      <p className="font-semibold text-[12px] text-gray-800 truncate" title={linkUser || ""}>
        {linkUser || "—"}
      </p>
      <div className="mt-0.5">
        <PasswordCopy password={linkPassword} />
      </div>
    </td>
  );
}

// Read-only presentation helpers for the "Empresa asociada" panel.
function ReadField({ label, value, full, secret }) {
  const [revealed, setRevealed] = useState(false);
  const hasValue = value !== undefined && value !== null && value !== "";
  const display = !hasValue
    ? "—"
    : secret && !revealed
    ? "••••••••"
    : value;
  return (
    <div className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <label className="text-[12px] text-gray-400">{label}</label>
      <div className="flex items-center gap-2">
        <p className="flex-1 text-[13px] text-gray-700 bg-gray-50 px-3 py-2 rounded-[6px] break-words whitespace-pre-wrap min-h-[37px]">
          {display}
        </p>
        {secret && hasValue && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title={revealed ? "Ocultar" : "Mostrar"}
          >
            {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
    </div>
  );
}

function ReadSection({ title, children }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] text-nemu-azul font-semibold uppercase tracking-wide">{title}</p>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

const EMPTY_FORM = {
  username: "",
  email: "",
  password: "",
  role: "user",
  birthday: "",
  active: false,
};

function TransferModal({ fromUser, allUsers, onClose, onTransfer }) {
  const developments = fromUser.developments ?? [];
  const options = allUsers.filter((u) => u.id !== fromUser.id);

  const [toUserId, setToUserId] = useState("");
  const [selected, setSelected] = useState(new Set(developments.map((d) => d.id)));
  const [transferring, setTransferring] = useState(false);

  const toUser = options.find((u) => u.id === Number(toUserId));
  const allChecked = selected.size === developments.length && developments.length > 0;
  const someChecked = selected.size > 0 && selected.size < developments.length;

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(developments.map((d) => d.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!toUserId || selected.size === 0) return;
    setTransferring(true);
    try {
      await onTransfer(fromUser.id, Number(toUserId), [...selected]);
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-[8px] w-full max-w-[480px] shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-nemu-azul" />
            <h2 className="text-[18px] font-[500]">Transferir desarrollos</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
          {/* Origen */}
          <div className="flex flex-col gap-1">
            <p className="text-[12px] text-gray-400 font-medium uppercase tracking-wide">Origen</p>
            <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-[6px]">
              <div>
                <p className="text-[14px] font-medium">{fromUser.username}</p>
                <p className="text-[12px] text-gray-400">{fromUser.enterprise?.name || fromUser.email}</p>
              </div>
              <span className="text-[13px] font-medium text-nemu-azul bg-nemu-azul/10 px-2.5 py-1 rounded-full">
                {developments.length} desarrollo{developments.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* Lista de desarrollos */}
          {developments.length === 0 ? (
            <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-[6px] text-[13px] text-gray-500">
              Este usuario no tiene desarrollos para transferir.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-[12px] text-gray-400 font-medium uppercase tracking-wide">
                  Seleccionar desarrollos
                </p>
                <span className="text-[12px] text-gray-400">
                  {selected.size} de {developments.length} seleccionados
                </span>
              </div>

              {/* Seleccionar todos */}
              <label className="flex items-center gap-2.5 px-3 py-2 rounded-[6px] border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-nemu-azul"
                />
                <span className="text-[13px] font-medium text-gray-600">Seleccionar todos</span>
              </label>

              {/* Lista individual */}
              <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto border border-gray-100 rounded-[6px] p-1">
                {developments.map((dev) => (
                  <label
                    key={dev.id}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-[4px] cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(dev.id)}
                      onChange={() => toggleOne(dev.id)}
                      className="w-4 h-4 accent-nemu-azul shrink-0"
                    />
                    <span className="text-[13px] text-gray-700 truncate">
                      {dev.name || `Desarrollo #${dev.id}`}
                    </span>
                    {dev.status && (
                      <span className="ml-auto shrink-0 text-[11px] text-gray-400 font-mono">
                        {dev.status}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Destino */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-gray-400 font-medium uppercase tracking-wide">
              Transferir a
            </label>
            <select
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
            >
              <option value="">Seleccionar usuario destino...</option>
              {options.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}{u.enterprise?.name ? ` — ${u.enterprise.name}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Aviso */}
          {toUser && selected.size > 0 && (
            <div className="flex items-center gap-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-[6px] text-[13px] text-amber-700">
              <ArrowRightLeft size={14} className="shrink-0" />
              Se moverán <strong>{selected.size}</strong> desarrollo{selected.size !== 1 ? "s" : ""} de{" "}
              <strong>{fromUser.username}</strong> a <strong>{toUser.username}</strong>.
              Esta acción no se puede deshacer.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[14px] border rounded-[6px] text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!toUserId || selected.size === 0 || transferring}
            className="flex items-center gap-2 px-4 py-2 text-[14px] bg-nemu-azul text-white rounded-[6px] hover:bg-nemu-azulOscuro transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowRightLeft size={14} />
            {transferring ? "Transfiriendo..." : `Transferir ${selected.size > 0 ? selected.size : ""} desarrollo${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ mode, user, onClose, onSave }) {
  const [form, setForm] = useState(
    mode === "edit" ? { ...user, password: "" } : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.username.trim() || !form.email.trim()) {
      setError("Nombre de usuario y email son requeridos.");
      return;
    }
    if (mode === "create" && !form.password.trim()) {
      setError("La contraseña es requerida al crear un usuario.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (mode === "edit" && !payload.password) delete payload.password;
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.message || "Ocurrió un error.");
    } finally {
      setSaving(false);
    }
  };

  const enterprise = user?.enterprise;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-[8px] w-full max-w-[520px] shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-[18px] font-[500]">
            {mode === "create" ? "Agregar usuario" : "Editar usuario"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-[13px] rounded-[6px]">
              {error}
            </div>
          )}

          {/* User fields */}
          <div className="flex flex-col gap-1">
            <label className="text-[13px] text-gray-500">Nombre de usuario *</label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              placeholder="Nombre de usuario"
              className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[13px] text-gray-500">Email *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[13px] text-gray-500">
              {mode === "create"
                ? "Contraseña *"
                : "Nueva contraseña (dejar vacío para no cambiar)"}
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-[13px] text-gray-500">Rol</label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[13px] text-gray-500">Fecha de nacimiento</label>
              <input
                type="date"
                name="birthday"
                value={form.birthday || ""}
                onChange={handleChange}
                className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="active"
              checked={form.active}
              onChange={handleChange}
              className="w-4 h-4 accent-nemu-azul"
            />
            <span className="text-[14px]">Cuenta activa</span>
          </label>

          {/* Enterprise info (read-only, only in edit mode) */}
          {mode === "edit" && enterprise && (
            <div className="mt-1 flex flex-col gap-5 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-[13px] text-gray-400 font-medium">
                <Building2 size={14} />
                Empresa asociada (solo lectura)
              </div>

              {/* Información de la empresa */}
              <ReadSection title="Información de la empresa">
                <ReadField label="Giro" value={enterprise.giro} />
                <ReadField label="Nombre de la empresa" value={enterprise.name} />
                <ReadField label="Ciudad" value={entLocation(enterprise)} />
                <ReadField label="Sitio web" value={enterprise.website} />
                {enterprise.giro === "Desarrolladora" && (
                  <ReadField label="No. de proyectos entregados" value={enterprise.delivered_projects} />
                )}
                <ReadField label="Descripción" value={enterprise.enterprise_description} full />
                {enterprise.giro === "Desarrolladora" && (
                  <>
                    <ReadField label="Cesión de derechos" value={enterprise.rights_transfer ? "Sí" : "No"} />
                    {enterprise.rights_transfer && (
                      <ReadField
                        label="Porcentaje de cesión"
                        value={enterprise.cesion_porcentaje != null ? `${parseFloat(enterprise.cesion_porcentaje)}%` : ""}
                      />
                    )}
                  </>
                )}
              </ReadSection>

              {/* Contacto Principal */}
              <ReadSection title="Contacto Principal">
                <ReadField label="Nombre del contacto" value={enterprise.contact_name} />
                <ReadField label="Cargo" value={enterprise.contact_position} />
                <ReadField label="Teléfono" value={entPhone(enterprise)} />
                <ReadField label="Correo" value={entEmail(enterprise)} />
              </ReadSection>

              {/* Inventario */}
              <ReadSection title="Inventario">
                <ReadField label="Tipo de inventario" value={enterprise.inventory_type} full />
                {enterprise.inventory_type === "Drive/Link/App" && (
                  <>
                    <ReadField label="Link de inventario" value={enterprise.inventory_link} />
                    <ReadField label="Link de materiales" value={enterprise.material_url} />
                    <ReadField label="Usuario" value={enterprise.link_user} />
                    <ReadField label="Contraseña" value={enterprise.link_password} secret />
                  </>
                )}
                {enterprise.inventory_type === "Grupo de WhatsApp" && (
                  <ReadField label="Grupo de WhatsApp" value={enterprise.whatsapp_group} full />
                )}
              </ReadSection>

              {/* Adicional */}
              <ReadSection title="Adicional">
                <ReadField label="Recursos adicionales" value={enterprise.extras} />
                <ReadField label="Notas internas" value={enterprise.comentarios} />
              </ReadSection>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[14px] border rounded-[6px] text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-[14px] bg-nemu-azul text-white rounded-[6px] hover:bg-nemu-azulOscuro transition-colors disabled:opacity-50"
            >
              {saving
                ? "Guardando..."
                : mode === "create"
                ? "Crear usuario"
                : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CmsUsers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterGiro, setFilterGiro] = useState("all");
  const [filterActive, setFilterActive] = useState("all");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [modal, setModal] = useState(null);
  const [transferFrom, setTransferFrom] = useState(null);
  const [view, setView] = useState("empresas"); // "empresas" | "desarrollos"

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const getSortValue = (u, key) => {
    switch (key) {
      case "username": return (u.username || "").toLowerCase();
      case "enterprise.name": return (u.enterprise?.name || "").toLowerCase();
      case "enterprise.giro": return (u.enterprise?.giro || "").toLowerCase();
      case "role": return (u.role || "").toLowerCase();
      case "active": return u.active ? 1 : 0;
      case "createdAt": return u.createdAt ? new Date(u.createdAt).getTime() : 0;
      case "devCount": return u.developments?.length ?? 0;
      default: return "";
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    fetchUsers();
  }, [user, authLoading]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminGetAllUsers();
      setUsers(response.data.data?.users || []);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedUsers = (() => {
    const filtered = users.filter((u) => {
    const matchesSearch =
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.enterprise?.name?.toLowerCase().includes(search.toLowerCase()) ||
      entEmail(u.enterprise)?.toLowerCase().includes(search.toLowerCase());
    const matchesRole = filterRole === "all" || u.role === filterRole;
    const matchesGiro =
      filterGiro === "all" || u.enterprise?.giro === filterGiro;
      const matchesActive =
        filterActive === "all" ||
        (filterActive === "active" ? u.active : !u.active);
      return matchesSearch && matchesRole && matchesGiro && matchesActive;
    });

    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  })();

  // Vista "Desarrollos": se aplana la lista de todos los desarrollos de todos
  // los usuarios, adjuntando la empresa (enterprise) dueña de cada uno.
  const allDevelopments = users.flatMap((u) =>
    (u.developments || []).map((d) => ({ ...d, enterprise: u.enterprise }))
  );

  const devCounts = {
    total: allDevelopments.length,
    active: allDevelopments.filter((d) => d.status === "Publicado").length,
    sold: allDevelopments.filter((d) => d.status === "Vendido").length,
    draft: allDevelopments.filter((d) => d.status === "Borrador").length,
  };

  const getDevSortValue = (d, key) => {
    switch (key) {
      case "dev.name": return (d.name || "").toLowerCase();
      case "enterprise.name": return (d.enterprise?.name || "").toLowerCase();
      case "enterprise.giro": return (d.enterprise?.giro || "").toLowerCase();
      case "lastInventoryUpdate": return d.lastInventoryUpdate ? new Date(d.lastInventoryUpdate).getTime() : 0;
      case "enterprise.contact_name": return (d.enterprise?.contact_name || "").toLowerCase();
      default: return null; // clave no aplicable a desarrollos → sin orden
    }
  };

  const filteredDevelopments = (() => {
    const q = search.toLowerCase();
    const filtered = allDevelopments.filter((d) =>
      d.name?.toLowerCase().includes(q) ||
      d.enterprise?.name?.toLowerCase().includes(q)
    );
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getDevSortValue(a, sortKey);
      const bv = getDevSortValue(b, sortKey);
      if (av === null || bv === null) return 0;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  })();

  const handleCreate = async (data) => {
    const response = await adminCreateUser(data);
    if (response.data?.success === false) {
      throw new Error(response.data.message);
    }
    await fetchUsers();
    setModal(null);
    Swal.fire({
      icon: "success",
      title: "Usuario creado",
      showConfirmButton: false,
      timer: 1500,
    });
  };

  const handleUpdate = async (data) => {
    const response = await adminUpdateUser(modal.user.id, data);
    if (response.data?.success === false) {
      throw new Error(response.data.message);
    }
    await fetchUsers();
    setModal(null);
    Swal.fire({
      icon: "success",
      title: "Usuario actualizado",
      showConfirmButton: false,
      timer: 1500,
    });
  };

  const handleTransfer = async (fromUserId, toUserId, developmentIds) => {
    try {
      const response = await adminTransferDevelopments(fromUserId, toUserId, developmentIds);
      setTransferFrom(null);
      await fetchUsers();
      Swal.fire({
        icon: "success",
        title: "Transferencia completada",
        text: response.data?.message || "Desarrollos transferidos correctamente",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo completar la transferencia.",
      });
    }
  };

  const handleDelete = async (u) => {
    const result = await Swal.fire({
      title: "Eliminar usuario",
      text: `¿Eliminar a "${u.username}"? Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    try {
      await adminDeleteUser(u.id);
      await fetchUsers();
      Swal.fire({
        icon: "success",
        title: "Usuario eliminado",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "No se pudo eliminar el usuario.",
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getActiveBadge = (active) => (
    <span
      className={`px-2 py-1 rounded-full text-[11px] font-medium ${
        active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {active ? "Activo" : "Inactivo"}
    </span>
  );

  const getGiroBadge = (giro) => {
    if (!giro) return <span className="text-gray-300 text-[12px]">—</span>;
    const isInmo = giro === "Inmobiliaria";
    return (
      <span
        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
          isInmo
            ? "bg-blue-50 text-blue-600"
            : "bg-purple-50 text-purple-600"
        }`}
      >
        {giro}
      </span>
    );
  };

  return (
    <main className="w-[100vw] max-w-[100vw] bg-[#eaeaea]">
      <Header />
      <div className="flex h-full w-full pr-[20px] pb-[20px]">
        <Navigator />
        <div className="flex flex-col w-full max-h-[calc(100svh-120px)] overflow-y-auto bg-white rounded-[4px]">
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-200 flex items-start justify-between">
            <div className="flex flex-col gap-3">
              <h1 className="text-[24px] font-[500]">{view === "empresas" ? "Usuarios CMS" : "Desarrollos"}</h1>
              {/* Switch Empresas / Desarrollos */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-[8px] w-max">
                <button
                  type="button"
                  onClick={() => setView("empresas")}
                  className={`px-4 py-1.5 rounded-[6px] text-[13px] font-medium transition-colors ${
                    view === "empresas" ? "bg-white text-nemu-black shadow-sm" : "text-gray-500 hover:text-nemu-black"
                  }`}
                >
                  Empresas
                </button>
                <button
                  type="button"
                  onClick={() => setView("desarrollos")}
                  className={`px-4 py-1.5 rounded-[6px] text-[13px] font-medium transition-colors ${
                    view === "desarrollos" ? "bg-white text-nemu-black shadow-sm" : "text-gray-500 hover:text-nemu-black"
                  }`}
                >
                  Desarrollos
                </button>
              </div>
            </div>
            <button
              onClick={() => setModal({ mode: "create" })}
              className="flex items-center gap-2 px-4 py-2 bg-nemu-azul text-white text-[14px] rounded-[6px] hover:bg-nemu-azulOscuro transition-colors"
            >
              <Plus size={16} />
              Agregar usuario
            </button>
          </div>

          {/* Filters */}
          <div className="px-6 pt-4 pb-3 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder={view === "empresas" ? "Buscar por usuario, email o empresa..." : "Buscar por desarrollo o empresa..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-[220px] px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
            />
            {view === "empresas" && (
              <>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
                >
                  <option value="all">Todos los roles</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <select
                  value={filterGiro}
                  onChange={(e) => setFilterGiro(e.target.value)}
                  className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
                >
                  <option value="all">Todos los giros</option>
                  {GIROS.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-nemu-azul"
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>
              </>
            )}
            <span className="text-[13px] text-gray-500">
              {view === "empresas"
                ? `${filteredAndSortedUsers.length} usuario${filteredAndSortedUsers.length !== 1 ? "s" : ""}`
                : `${filteredDevelopments.length} desarrollo${filteredDevelopments.length !== 1 ? "s" : ""}`}
            </span>
          </div>

          {/* Summary */}
          {view === "empresas" ? (
            <div className="px-6 py-4 grid grid-cols-3 gap-4 border-b border-gray-100">
              <div className="flex flex-col gap-1 px-4 py-3 bg-gray-50 rounded-[6px]">
                <p className="text-[12px] text-gray-400">Total de usuarios</p>
                <p className="text-[26px] font-[600] text-nemu-black leading-none">{users.length}</p>
              </div>
              <div className="flex flex-col gap-1 px-4 py-3 bg-blue-50 rounded-[6px]">
                <p className="text-[12px] text-blue-400">Inmobiliarias</p>
                <p className="text-[26px] font-[600] text-blue-600 leading-none">
                  {users.filter((u) => u.enterprise?.giro === "Inmobiliaria").length}
                </p>
              </div>
              <div className="flex flex-col gap-1 px-4 py-3 bg-purple-50 rounded-[6px]">
                <p className="text-[12px] text-purple-400">Desarrolladoras</p>
                <p className="text-[26px] font-[600] text-purple-600 leading-none">
                  {users.filter((u) => u.enterprise?.giro === "Desarrolladora").length}
                </p>
              </div>
            </div>
          ) : (
            <div className="px-6 py-4 grid grid-cols-4 gap-4 border-b border-gray-100">
              <div className="flex flex-col gap-1 px-4 py-3 bg-gray-50 rounded-[6px]">
                <p className="text-[12px] text-gray-400">Total de desarrollos</p>
                <p className="text-[26px] font-[600] text-nemu-black leading-none">{devCounts.total}</p>
              </div>
              <div className="flex flex-col gap-1 px-4 py-3 bg-green-50 rounded-[6px]">
                <p className="text-[12px] text-green-500">Desarrollos activos</p>
                <p className="text-[26px] font-[600] text-green-600 leading-none">{devCounts.active}</p>
              </div>
              <div className="flex flex-col gap-1 px-4 py-3 bg-amber-50 rounded-[6px]">
                <p className="text-[12px] text-amber-500">Desarrollos vendidos</p>
                <p className="text-[26px] font-[600] text-amber-600 leading-none">{devCounts.sold}</p>
              </div>
              <div className="flex flex-col gap-1 px-4 py-3 bg-blue-50 rounded-[6px]">
                <p className="text-[12px] text-blue-400">Desarrollos en borrador</p>
                <p className="text-[26px] font-[600] text-blue-600 leading-none">{devCounts.draft}</p>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="px-6 py-4 overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nemu-azul"></div>
              </div>
            ) : view === "desarrollos" ? (
              filteredDevelopments.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  No se encontraron desarrollos
                </div>
              ) : (
                <table className="w-full text-[13px] min-w-[1200px]">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <SortableTh label="Nombre desarrollo" sortKey="dev.name" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                      <SortableTh label="Empresa" sortKey="enterprise.name" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                      <SortableTh label="Giro" sortKey="enterprise.giro" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                      <th className="pb-3 font-medium pr-4">Ciudad</th>
                      <SortableTh label="Última act. inventario" sortKey="lastInventoryUpdate" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                      <th className="pb-3 font-medium pr-4">Ubicación inventario</th>
                      <th className="pb-3 font-medium pr-4">Link inventario</th>
                      <th className="pb-3 font-medium pr-4">Accesos inventario</th>
                      <SortableTh label="Contacto principal" sortKey="enterprise.contact_name" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                      <th className="pb-3 font-medium pr-4">Teléfono contacto</th>
                      <th className="pb-3 font-medium pr-4">Recursos adicionales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevelopments.map((d) => {
                      const ent = d.enterprise;
                      return (
                        <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          {/* Nombre desarrollo */}
                          <td className="py-3 pr-4 font-medium text-[13px]">{d.name || "—"}</td>

                          {/* Empresa */}
                          <td className="py-3 pr-4 text-[13px]">
                            {ent?.name || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Giro */}
                          <td className="py-3 pr-4">{getGiroBadge(ent?.giro)}</td>

                          {/* Ciudad */}
                          <td className="py-3 pr-4 text-gray-500 text-[12px]">
                            {ent?.city || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Última actualización de inventario */}
                          <td className="py-3 pr-4 text-gray-500 text-[12px] whitespace-nowrap">
                            {formatDate(d.lastInventoryUpdate)}
                          </td>

                          {/* Ubicación de inventario */}
                          <td className="py-3 pr-4 text-gray-500 text-[12px]">
                            {ent?.inventory_type || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Link inventario */}
                          <td className="py-3 pr-4 max-w-[180px]">
                            {ent?.inventory_link ? (
                              <a
                                href={ent.inventory_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={ent.inventory_link}
                                className="block truncate text-nemu-azul text-[12px] hover:underline"
                              >
                                {ent.inventory_link}
                              </a>
                            ) : (
                              <span className="text-gray-300 text-[12px]">—</span>
                            )}
                          </td>

                          {/* Accesos inventario (usuario + contraseña) */}
                          <AccessCell linkUser={ent?.link_user} linkPassword={ent?.link_password} />

                          {/* Nombre contacto principal */}
                          <td className="py-3 pr-4 text-gray-500 text-[12px]">
                            {ent?.contact_name || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Teléfono contacto */}
                          <td className="py-3 pr-4 text-gray-500 text-[12px]">
                            {ent?.contact_phone || <span className="text-gray-300">—</span>}
                          </td>

                          {/* Recursos adicionales */}
                          <td className="py-3 pr-4 max-w-[220px]">
                            <span className="block truncate text-gray-500 text-[12px]" title={ent?.extras || ""}>
                              {ent?.extras || <span className="text-gray-300">—</span>}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : filteredAndSortedUsers.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                No se encontraron usuarios
              </div>
            ) : (
              <table className="w-full text-[13px] min-w-[900px]">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <SortableTh label="Nombre" sortKey="username" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <SortableTh label="Empresa" sortKey="enterprise.name" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <SortableTh label="Giro" sortKey="enterprise.giro" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <th className="pb-3 font-medium pr-4">Ciudad</th>
                    <SortableTh label="No. Desarrollos" sortKey="devCount" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <th className="pb-3 font-medium pr-4">Teléfono</th>
                    <th className="pb-3 font-medium pr-4">Link inventario</th>
                    <th className="pb-3 font-medium pr-4">Accesos inventario</th>
                    <SortableTh label="Estado" sortKey="active" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <SortableTh label="Fecha de registro" sortKey="createdAt" current={sortKey} dir={sortDir} onSort={handleSort} className="pr-4" />
                    <th className="pb-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedUsers.map((u) => {
                    const ent = u.enterprise;
                    return (
                      <tr
                        key={u.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        {/* Nombre */}
                        <td className="py-3 pr-4">
                          <p className="font-medium text-[14px]">
                            {u.username || "—"}
                          </p>
                          <p className="text-gray-400 text-[12px]">{u.email}</p>
                        </td>

                        {/* Empresa */}
                        <td className="py-3 pr-4">
                          {ent?.name ? (
                            <p className="font-medium text-[13px]">{ent.name}</p>
                          ) : (
                            <span className="text-gray-300 text-[12px]">Sin empresa</span>
                          )}
                        </td>

                        {/* Giro */}
                        <td className="py-3 pr-4">{getGiroBadge(ent?.giro)}</td>

                        {/* Ciudad */}
                        <td className="py-3 pr-4 max-w-[180px]">
                          <span
                            className="block truncate text-gray-500 text-[12px]"
                            title={ent?.city || ""}
                          >
                            {ent?.city || <span className="text-gray-300">—</span>}
                          </span>
                        </td>

                        {/* Desarrollos (activos / total) */}
                        <td className="py-3 pr-4">
                          {(() => {
                            const devs = u.developments || [];
                            const total = devs.length;
                            const active = devs.filter((d) => d.status === "Publicado").length;
                            return (
                              <span className={`text-[13px] font-medium ${total > 0 ? "text-nemu-black" : "text-gray-300"}`}>
                                {active}/{total}
                              </span>
                            );
                          })()}
                        </td>

                        {/* Teléfono */}
                        <td className="py-3 pr-4 text-gray-500 text-[12px]">
                          {entPhone(ent) || <span className="text-gray-300">—</span>}
                        </td>

                        {/* Link inventario */}
                        <td className="py-3 pr-4 max-w-[180px]">
                          {ent?.inventory_link ? (
                            <a
                              href={ent.inventory_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={ent.inventory_link}
                              className="block truncate text-nemu-azul text-[12px] hover:underline"
                            >
                              {ent.inventory_link}
                            </a>
                          ) : (
                            <span className="text-gray-300 text-[12px]">—</span>
                          )}
                        </td>

                        {/* Accesos */}
                        <AccessCell linkUser={ent?.link_user} linkPassword={ent?.link_password} />

                        {/* Estado */}
                        <td className="py-3 pr-4">{getActiveBadge(u.active)}</td>

                        {/* Fecha de registro */}
                        <td className="py-3 pr-4 text-gray-400 text-[12px]">{formatDate(u.createdAt)}</td>

                        {/* Acciones */}
                        <td className="py-3">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => setTransferFrom(u)}
                              className="p-1.5 rounded-[4px] text-gray-500 hover:bg-blue-50 hover:text-nemu-azul transition-colors"
                              title="Transferir desarrollos"
                            >
                              <ArrowRightLeft size={15} />
                            </button>
                            <button
                              onClick={() => setModal({ mode: "edit", user: u })}
                              className="p-1.5 rounded-[4px] text-gray-500 hover:bg-gray-100 hover:text-nemu-azul transition-colors"
                              title="Editar"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => handleDelete(u)}
                              className="p-1.5 rounded-[4px] text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          onClose={() => setModal(null)}
          onSave={modal.mode === "create" ? handleCreate : handleUpdate}
        />
      )}

      {transferFrom && (
        <TransferModal
          fromUser={transferFrom}
          allUsers={users}
          onClose={() => setTransferFrom(null)}
          onTransfer={handleTransfer}
        />
      )}
    </main>
  );
}

export { CmsUsers };
